import React, { useState, useEffect, JSX } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider,
  SelectChangeEvent,
  Card,
  CardContent,
} from '@mui/material';
import {
  Visibility,
  Edit,
  Delete,
  ContactMail,
  CheckCircle,
  Cancel,
  Pending,
  MoreVert,
  Email,
  Phone,
  Business,
  Search,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import {
  getAllDemoRequests,
  getDemoRequestStats,
  updateDemoRequest,
  deleteDemoRequest,
  DemoRequest,
  DemoRequestStats,
} from '../services/demoRequest';

const DemoRequestsPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [demoRequests, setDemoRequests] = useState<DemoRequest[]>([]);
  const [stats, setStats] = useState<DemoRequestStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRequest, setSelectedRequest] = useState<DemoRequest | null>(null);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [editData, setEditData] = useState({ status: '', notes: '' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchDemoRequests();
    }, 300);

    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, filterStatus, page, rowsPerPage]);

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDemoRequests = async () => {
    try {
      setLoading(true);
      const params: any = { page: page + 1, limit: rowsPerPage };
      if (filterStatus) params.status = filterStatus;

      const response = await getAllDemoRequests(params);
      
      // Client-side filtering for search
      let filteredData = response.data;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filteredData = response.data.filter((request: DemoRequest) =>
          request.name.toLowerCase().includes(query) ||
          request.email.toLowerCase().includes(query) ||
          request.contactNumber.toLowerCase().includes(query) ||
          request.institute.toLowerCase().includes(query)
        );
      }
      
      setDemoRequests(filteredData);
      setTotalCount(searchQuery ? filteredData.length : response.pagination.total);
    } catch (err: any) {
      enqueueSnackbar(err.message || 'Failed to fetch demo requests', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await getDemoRequestStats();
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, request: DemoRequest) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedRequest(request);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleViewRequest = () => {
    handleMenuClose();
    setOpenViewDialog(true);
  };

  const handleEditRequest = () => {
    if (selectedRequest) {
      setEditData({
        status: selectedRequest.status,
        notes: selectedRequest.notes || '',
      });
      handleMenuClose();
      setOpenEditDialog(true);
    }
  };

  const handleDeleteRequest = () => {
    handleMenuClose();
    setOpenDeleteDialog(true);
  };

  const handleUpdateRequest = async () => {
    if (!selectedRequest) return;

    try {
      await updateDemoRequest(selectedRequest._id, editData);
      setOpenEditDialog(false);
      enqueueSnackbar('Demo request updated successfully', { variant: 'success' });
      fetchDemoRequests();
      fetchStats();
    } catch (err: any) {
      enqueueSnackbar(err.message || 'Failed to update demo request', { variant: 'error' });
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedRequest) return;

    try {
      await deleteDemoRequest(selectedRequest._id);
      setOpenDeleteDialog(false);
      enqueueSnackbar('Demo request deleted successfully', { variant: 'success' });
      fetchDemoRequests();
      fetchStats();
    } catch (err: any) {
      enqueueSnackbar(err.message || 'Failed to delete demo request', { variant: 'error' });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'contacted':
        return 'info';
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string): JSX.Element | undefined => {
    switch (status) {
      case 'pending':
        return <Pending sx={{ fontSize: 16 }} />;
      case 'contacted':
        return <ContactMail sx={{ fontSize: 16 }} />;
      case 'completed':
        return <CheckCircle sx={{ fontSize: 16 }} />;
      case 'cancelled':
        return <Cancel sx={{ fontSize: 16 }} />;
      default:
        return undefined;
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Demo Requests
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flex: 1, justifyContent: 'flex-end' }}>
          <TextField
            size="small"
            placeholder="Search demo requests..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filter by Status</InputLabel>
            <Select
              value={filterStatus}
              onChange={(e: SelectChangeEvent) => {
                setFilterStatus(e.target.value);
                setPage(0);
              }}
              label="Filter by Status"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="contacted">Contacted</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Stats Cards */}
      {stats && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' },
            gap: 2,
            mb: 3,
          }}
        >
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Total Requests
              </Typography>
              <Typography variant="h4" component="div">
                {stats.total}
              </Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Pending
              </Typography>
              <Typography variant="h4" component="div" color="warning.main">
                {stats.pending}
              </Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Contacted
              </Typography>
              <Typography variant="h4" component="div" color="info.main">
                {stats.contacted}
              </Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Completed
              </Typography>
              <Typography variant="h4" component="div" color="success.main">
                {stats.completed}
              </Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Cancelled
              </Typography>
              <Typography variant="h4" component="div" color="error.main">
                {stats.cancelled}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Institute</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <Typography color="textSecondary">Loading...</Typography>
                  </TableCell>
                </TableRow>
              ) : demoRequests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <Typography color="textSecondary">No demo requests found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                demoRequests.map((request) => (
                  <TableRow key={request._id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {request.name}
                      </Typography>
                    </TableCell>
                    <TableCell>{request.institute}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Email sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2">{request.email}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Phone sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2">{request.contactNumber}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        {...(getStatusIcon(request.status) && { icon: getStatusIcon(request.status) })}
                        label={request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                        color={getStatusColor(request.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(request.createdAt).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, request)}
                      >
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Action Menu */}
      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleViewRequest}>
          <ListItemIcon>
            <Visibility fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleEditRequest}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Update Status</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleDeleteRequest} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* View Dialog */}
      <Dialog open={openViewDialog} onClose={() => setOpenViewDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Demo Request Details</DialogTitle>
        <DialogContent dividers>
          {selectedRequest && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="caption" color="textSecondary" fontWeight={600}>
                  Name
                </Typography>
                <Typography variant="body1">{selectedRequest.name}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary" fontWeight={600}>
                  Institute
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                  <Business sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="body1">{selectedRequest.institute}</Typography>
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary" fontWeight={600}>
                  Email
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                  <Email sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="body1">{selectedRequest.email}</Typography>
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary" fontWeight={600}>
                  Contact Number
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                  <Phone sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="body1">{selectedRequest.contactNumber}</Typography>
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary" fontWeight={600}>
                  Status
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    {...(getStatusIcon(selectedRequest.status) && {
                      icon: getStatusIcon(selectedRequest.status),
                    })}
                    label={
                      selectedRequest.status.charAt(0).toUpperCase() +
                      selectedRequest.status.slice(1)
                    }
                    color={getStatusColor(selectedRequest.status) as any}
                    size="small"
                  />
                </Box>
              </Box>
              {selectedRequest.notes && (
                <Box>
                  <Typography variant="caption" color="textSecondary" fontWeight={600}>
                    Notes
                  </Typography>
                  <Typography variant="body1">{selectedRequest.notes}</Typography>
                </Box>
              )}
              <Box>
                <Typography variant="caption" color="textSecondary" fontWeight={600}>
                  Submitted On
                </Typography>
                <Typography variant="body1">
                  {new Date(selectedRequest.createdAt).toLocaleString()}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenViewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Demo Request</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={editData.status}
                onChange={(e: SelectChangeEvent) =>
                  setEditData({ ...editData, status: e.target.value })
                }
                label="Status"
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="contacted">Contacted</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Notes"
              value={editData.notes}
              onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
              placeholder="Add any notes about this demo request..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
          <Button onClick={handleUpdateRequest} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this demo request? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DemoRequestsPage;
